import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router"
import { match } from "ts-pattern"
import { useStore, useT } from "../stores/StoreProvider.js"
import { mediaUrl } from "../lib/api.js"
import { getTelegramWebApp } from "../lib/telegram.js"
import {
  ChevronRight,
  Clock,
  Eye,
  EyeOff,
  Lock,
  Star,
  Trash2,
  Grid3X3,
  ImageIcon,
  Users,
} from "lucide-react"

type SettingsSection = "main" | "visibility" | "layout" | "theme" | "cover" | "pending" | "contributors"

const visibilityOptions = [
  { value: "public", label: "Public", desc: "Anyone can view", icon: Eye },
  { value: "unlisted", label: "Unlisted", desc: "Only with link", icon: EyeOff },
  { value: "private", label: "Private", desc: "Invited only", icon: Lock },
  { value: "paid", label: "Paid", desc: "Requires payment", icon: Star },
] as const

const layoutOptions = [
  { value: "grid", label: "Grid", desc: "Square thumbnails" },
  { value: "masonry", label: "Masonry", desc: "Natural aspect ratios" },
] as const

const columnOptions = [2, 3, 4] as const
const gapOptions = ["none", "sm", "md", "lg"] as const
const radiusOptions = ["none", "sm", "md", "lg"] as const

export const GallerySettingsPage = observer(function GallerySettingsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { galleryStore } = useStore()
  const t = useT()
  const [section, setSection] = useState<SettingsSection>("main")
  const [saving, setSaving] = useState(false)

  // Local form state
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [visibility, setVisibility] = useState("public")
  const [pricingType, setPricingType] = useState("free")
  const [priceStars, setPriceStars] = useState(0)
  const [autoApproveGroup, setAutoApproveGroup] = useState(false)
  const [layoutType, setLayoutType] = useState<string>("grid")
  const [layoutColumns, setLayoutColumns] = useState<number>(3)
  const [layoutGap, setLayoutGap] = useState<string>("sm")
  const [layoutRadius, setLayoutRadius] = useState<string>("sm")

  const gallery =
    galleryStore.detailState.status === "loaded"
      ? galleryStore.detailState.gallery
      : null

  // Initialize form state from gallery
  useEffect(() => {
    if (gallery) {
      setTitle(gallery.title)
      setDescription(gallery.description ?? "")
      setVisibility(gallery.visibility)
      setPricingType(gallery.pricingType)
      setPriceStars(gallery.priceStars)
      try {
        const config = JSON.parse(gallery.themeConfig ?? "{}")
        setAutoApproveGroup(!!config.autoApproveGroup)
        if (config.layout) {
          if (config.layout.type) setLayoutType(config.layout.type)
          if (config.layout.columns) setLayoutColumns(config.layout.columns)
          if (config.layout.gap) setLayoutGap(config.layout.gap)
          if (config.layout.radius) setLayoutRadius(config.layout.radius)
        }
      } catch {
        setAutoApproveGroup(false)
      }
    }
  }, [gallery])

  // Telegram back button
  useEffect(() => {
    const webapp = getTelegramWebApp()
    if (!webapp) return
    webapp.BackButton.show()
    const goBack = () => {
      if (section !== "main") {
        setSection("main")
      } else {
        navigate(-1)
      }
    }
    webapp.BackButton.onClick(goBack)
    return () => {
      webapp.BackButton.offClick(goBack)
      webapp.BackButton.hide()
    }
  }, [section, navigate])

  const save = async (data: Record<string, unknown>) => {
    if (!id) return
    setSaving(true)
    await galleryStore.updateGallery(id, data as Parameters<typeof galleryStore.updateGallery>[1])
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!id) return
    const webapp = getTelegramWebApp()
    if (webapp) {
      webapp.showConfirm(t("settings.delete_confirm"), async (ok) => {
        if (ok) {
          await galleryStore.deleteGallery(id)
          navigate("/")
        }
      })
    } else {
      if (confirm("Delete this gallery?")) {
        await galleryStore.deleteGallery(id)
        navigate("/")
      }
    }
  }

  if (!gallery) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <p className="text-tg-hint text-sm">{t("common.loading")}</p>
      </div>
    )
  }

  return match(section)
    .with("main", () => (
      <div className="min-h-screen bg-tg-bg">
        <header className="sticky top-0 z-10 bg-tg-bg/80 backdrop-blur-lg border-b border-tg-secondary px-4 py-3">
          <h1 className="text-lg font-bold text-tg-text">{t("settings.gallery_settings")}</h1>
        </header>

        {/* Title & Description */}
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-tg-section-header uppercase tracking-wide block mb-1">
              {t("settings.title")}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => title !== gallery.title && save({ title })}
              className="w-full bg-tg-surface rounded-lg px-3 py-2.5 text-tg-text text-[15px] outline-none focus:ring-2 focus:ring-tg-accent/30"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-tg-section-header uppercase tracking-wide block mb-1">
              {t("settings.description")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => description !== (gallery.description ?? "") && save({ description })}
              rows={3}
              className="w-full bg-tg-surface rounded-lg px-3 py-2.5 text-tg-text text-[15px] outline-none resize-none focus:ring-2 focus:ring-tg-accent/30"
              placeholder={t("settings.description")}
            />
          </div>
        </div>

        {/* Settings Menu */}
        <div className="mt-2">
          <div className="px-4 py-1">
            <span className="text-xs font-medium text-tg-section-header uppercase tracking-wide">
              Settings
            </span>
          </div>

          <SettingsRow
            icon={Eye}
            label="Visibility"
            value={visibility}
            onClick={() => setSection("visibility")}
          />
          <SettingsRow
            icon={Grid3X3}
            label="Layout"
            value={layoutType.charAt(0).toUpperCase() + layoutType.slice(1)}
            onClick={() => setSection("layout")}
          />
          <SettingsRow
            icon={ImageIcon}
            label="Cover Photo"
            value={gallery.coverMediaId ? "Set" : "None"}
            onClick={() => setSection("cover")}
          />
          <SettingsRow
            icon={Clock}
            label="Pending Photos"
            value=""
            onClick={() => setSection("pending")}
          />
          <SettingsRow
            icon={Users}
            label="Contributors"
            value=""
            onClick={() => setSection("contributors")}
          />
          <SettingsToggleRow
            icon={Users}
            label="Auto-approve group uploads"
            checked={autoApproveGroup}
            onChange={(checked) => {
              setAutoApproveGroup(checked)
              try {
                const config = JSON.parse(gallery.themeConfig ?? "{}")
                config.autoApproveGroup = checked
                void save({ themeConfig: JSON.stringify(config) })
              } catch {
                void save({ themeConfig: JSON.stringify({ autoApproveGroup: checked }) })
              }
            }}
          />
        </div>

        {/* Pricing (only for paid) */}
        {visibility === "paid" && (
          <div className="mt-4 p-4">
            <label className="text-xs font-medium text-tg-section-header uppercase tracking-wide block mb-1">
              Price (Stars)
            </label>
            <input
              type="number"
              value={priceStars}
              onChange={(e) => setPriceStars(Number(e.target.value))}
              onBlur={() => save({ priceStars, pricingType: "one_time" })}
              min={1}
              className="w-full bg-tg-surface rounded-lg px-3 py-2.5 text-tg-text text-[15px] outline-none focus:ring-2 focus:ring-tg-accent/30"
            />
          </div>
        )}

        {/* Danger Zone */}
        <div className="mt-8 p-4">
          <button
            onClick={handleDelete}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-tg-destructive text-[15px] font-medium bg-tg-destructive/10 active:bg-tg-destructive/20 transition-colors"
            type="button"
          >
            <Trash2 className="w-4 h-4" />
            {t("settings.delete_gallery")}
          </button>
        </div>

        {saving && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-tg-text text-tg-bg px-4 py-2 rounded-full text-sm font-medium shadow-lg">
            {t("common.saving")}
          </div>
        )}
      </div>
    ))
    .with("visibility", () => (
      <div className="min-h-screen bg-tg-bg">
        <header className="sticky top-0 z-10 bg-tg-bg/80 backdrop-blur-lg border-b border-tg-secondary px-4 py-3">
          <h1 className="text-lg font-bold text-tg-text">Visibility</h1>
        </header>
        <div className="py-2">
          {visibilityOptions.map((opt) => {
            const Icon = opt.icon
            const selected = visibility === opt.value
            return (
              <button
                key={opt.value}
                onClick={() => {
                  setVisibility(opt.value)
                  const updates: Record<string, unknown> = { visibility: opt.value }
                  if (opt.value === "paid") {
                    updates.pricingType = "one_time"
                  } else {
                    updates.pricingType = "free"
                  }
                  void save(updates)
                  setSection("main")
                }}
                className="w-full flex items-center gap-3 px-4 py-3 active:bg-tg-secondary/50 transition-colors"
                type="button"
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${selected ? "bg-tg-accent text-tg-button-text" : "bg-tg-secondary text-tg-hint"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[15px] font-medium text-tg-text">{opt.label}</div>
                  <div className="text-[13px] text-tg-text-secondary">{opt.desc}</div>
                </div>
                {selected && (
                  <div className="w-5 h-5 rounded-full bg-tg-accent flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>
    ))
    .with("layout", () => {
      const saveLayout = (patch: Record<string, unknown>) => {
        const config = JSON.parse(gallery.themeConfig ?? "{}")
        config.layout = { ...config.layout, ...patch }
        void save({ themeConfig: JSON.stringify(config) })
      }
      return (
        <div className="min-h-screen bg-tg-bg">
          <header className="sticky top-0 z-10 bg-tg-bg/80 backdrop-blur-lg border-b border-tg-secondary px-4 py-3">
            <h1 className="text-lg font-bold text-tg-text">Layout</h1>
          </header>
          <div className="p-4 space-y-6">
            {/* Layout type */}
            <div>
              <label className="text-xs font-medium text-tg-section-header uppercase tracking-wide block mb-2">Type</label>
              <div className="grid grid-cols-2 gap-2">
                {layoutOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setLayoutType(opt.value)
                      saveLayout({ type: opt.value })
                    }}
                    className="bg-tg-surface rounded-xl p-3 text-center active:scale-[0.98] transition-transform border-2 border-transparent data-[selected]:border-tg-accent"
                    data-selected={opt.value === layoutType ? "" : undefined}
                    type="button"
                  >
                    <div className="text-[15px] font-medium text-tg-text">{opt.label}</div>
                    <div className="text-[12px] text-tg-hint mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Columns */}
            <div>
              <label className="text-xs font-medium text-tg-section-header uppercase tracking-wide block mb-2">Columns</label>
              <div className="flex gap-2">
                {columnOptions.map((n) => (
                  <button
                    key={n}
                    onClick={() => {
                      setLayoutColumns(n)
                      saveLayout({ columns: n })
                    }}
                    className="flex-1 bg-tg-surface rounded-xl py-2.5 text-center text-[15px] font-medium text-tg-text active:scale-[0.98] transition-transform border-2 border-transparent data-[selected]:border-tg-accent"
                    data-selected={n === layoutColumns ? "" : undefined}
                    type="button"
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Gap */}
            <div>
              <label className="text-xs font-medium text-tg-section-header uppercase tracking-wide block mb-2">Spacing</label>
              <div className="flex gap-2">
                {gapOptions.map((g) => (
                  <button
                    key={g}
                    onClick={() => {
                      setLayoutGap(g)
                      saveLayout({ gap: g })
                    }}
                    className="flex-1 bg-tg-surface rounded-xl py-2.5 text-center text-[13px] font-medium text-tg-text capitalize active:scale-[0.98] transition-transform border-2 border-transparent data-[selected]:border-tg-accent"
                    data-selected={g === layoutGap ? "" : undefined}
                    type="button"
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {/* Radius */}
            <div>
              <label className="text-xs font-medium text-tg-section-header uppercase tracking-wide block mb-2">Corners</label>
              <div className="flex gap-2">
                {radiusOptions.map((r) => (
                  <button
                    key={r}
                    onClick={() => {
                      setLayoutRadius(r)
                      saveLayout({ radius: r })
                    }}
                    className="flex-1 bg-tg-surface rounded-xl py-2.5 text-center text-[13px] font-medium text-tg-text capitalize active:scale-[0.98] transition-transform border-2 border-transparent data-[selected]:border-tg-accent"
                    data-selected={r === layoutRadius ? "" : undefined}
                    type="button"
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    })
    .with("cover", () => {
      const media =
        galleryStore.detailState.status === "loaded"
          ? galleryStore.detailState.media
          : []

      return (
        <div className="min-h-screen bg-tg-bg">
          <header className="sticky top-0 z-10 bg-tg-bg/80 backdrop-blur-lg border-b border-tg-secondary px-4 py-3">
            <h1 className="text-lg font-bold text-tg-text">Cover Photo</h1>
          </header>
          {media.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ImageIcon className="w-12 h-12 text-tg-hint/50 mb-3" />
              <p className="text-tg-hint text-sm">Add photos to set a cover</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-[2px] p-[2px]">
              {media.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    void save({ coverMediaId: m.id })
                    setSection("main")
                  }}
                  className={`aspect-square overflow-hidden relative ${gallery.coverMediaId === m.id ? "ring-2 ring-tg-accent ring-inset" : ""}`}
                  type="button"
                >
                  <img
                    src={mediaUrl(m.id)}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {gallery.coverMediaId === m.id && (
                    <div className="absolute inset-0 bg-tg-accent/20 flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full bg-tg-accent flex items-center justify-center">
                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )
    })
    .with("pending", () => {
      return <PendingPhotosSection galleryId={id!} />
    })
    .with("theme", () => null)
    .with("contributors", () => <ContributorsSection galleryId={id!} />)
    .exhaustive()
})

interface PendingMediaItem {
  id: string
  galleryId: string
  caption: string | null
  width: number | null
  height: number | null
  sortIndex: number
  proxyUrl: string
  createdAt: string
}

const PendingPhotosSection = observer(function PendingPhotosSection({ galleryId }: { galleryId: string }) {
  const { galleryStore } = useStore()
  const [pending, setPending] = useState<PendingMediaItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void (galleryStore.fetchPendingMedia(galleryId) as Promise<PendingMediaItem[]>).then((items) => {
      setPending(items)
      setLoading(false)
    })
  }, [galleryId, galleryStore])

  const handleApprove = async (mediaId: string) => {
    const ok = await (galleryStore.approveMedia(mediaId) as Promise<boolean>)
    if (ok) setPending(prev => prev.filter(m => m.id !== mediaId))
  }

  const handleReject = async (mediaId: string) => {
    const ok = await (galleryStore.rejectMedia(mediaId) as Promise<boolean>)
    if (ok) setPending(prev => prev.filter(m => m.id !== mediaId))
  }

  const t = useT()
  if (loading) return <div className="p-8 text-center text-tg-hint">{t("common.loading")}</div>
  if (pending.length === 0) return <div className="p-8 text-center text-tg-hint text-sm">{t("settings.no_pending")}</div>

  return (
    <div className="min-h-screen bg-tg-bg">
      <header className="sticky top-0 z-10 bg-tg-bg/80 backdrop-blur-lg border-b border-tg-secondary px-4 py-3">
        <h1 className="text-lg font-bold text-tg-text">Pending Photos ({pending.length})</h1>
      </header>
      <div className="grid grid-cols-2 gap-2 p-2">
        {pending.map((item) => (
          <div key={item.id} className="bg-tg-surface rounded-xl overflow-hidden">
            <img src={item.proxyUrl || mediaUrl(item.id)} alt="" className="w-full aspect-square object-cover" />
            <div className="flex gap-1 p-2">
              <button
                onClick={() => void handleApprove(item.id)}
                className="flex-1 py-2 rounded-lg bg-green-500/10 text-green-600 text-xs font-medium active:bg-green-500/20"
                type="button"
              >
                Approve
              </button>
              <button
                onClick={() => void handleReject(item.id)}
                className="flex-1 py-2 rounded-lg bg-red-500/10 text-red-600 text-xs font-medium active:bg-red-500/20"
                type="button"
              >
                Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
})

const ContributorsSection = observer(function ContributorsSection({ galleryId }: { galleryId: string }) {
  const { galleryStore } = useStore()
  const t = useT()

  useEffect(() => {
    void galleryStore.fetchContributors(galleryId)
  }, [galleryId, galleryStore])

  const gallery =
    galleryStore.detailState.status === "loaded"
      ? galleryStore.detailState.gallery
      : null

  const handleRemove = async (userId: string, displayName: string) => {
    const webapp = getTelegramWebApp()
    const doRemove = async () => {
      await (galleryStore.removeContributor(galleryId, userId) as Promise<boolean>)
    }
    if (webapp) {
      webapp.showConfirm(`Remove all photos by ${displayName}?`, (ok) => {
        if (ok) void doRemove()
      })
    } else {
      if (confirm(`Remove all photos by ${displayName}?`)) {
        void doRemove()
      }
    }
  }

  return (
    <div className="min-h-screen bg-tg-bg">
      <header className="sticky top-0 z-10 bg-tg-bg/80 backdrop-blur-lg border-b border-tg-secondary px-4 py-3">
        <h1 className="text-lg font-bold text-tg-text">Contributors</h1>
      </header>
      {match(galleryStore.contributorsState)
        .with({ status: "idle" }, { status: "loading" }, () => (
          <div className="p-8 text-center text-tg-hint">{t("common.loading")}</div>
        ))
        .with({ status: "error" }, (s) => (
          <div className="p-8 text-center text-tg-hint text-sm">{s.message}</div>
        ))
        .with({ status: "loaded" }, (s) => (
          s.contributors.length === 0 ? (
            <div className="p-8 text-center text-tg-hint text-sm">No contributors yet</div>
          ) : (
            <div className="py-2">
              {s.contributors.map((c) => {
                const isOwner = gallery ? c.user.id === gallery.ownerUserId : false
                return (
                  <div key={c.user.id} className="flex items-center gap-3 px-4 py-3">
                    {c.user.avatarUrl ? (
                      <img src={c.user.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-tg-secondary flex items-center justify-center text-tg-hint text-sm font-medium">
                        {c.user.displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-medium text-tg-text truncate">
                        {c.user.displayName}
                        {isOwner && <span className="text-tg-hint text-xs ml-1">(owner)</span>}
                      </div>
                      <div className="text-[13px] text-tg-hint">
                        {c.count} photo{c.count === 1 ? "" : "s"}
                        {c.user.username && ` · @${c.user.username}`}
                      </div>
                    </div>
                    {!isOwner && (
                      <button
                        onClick={() => void handleRemove(c.user.id, c.user.displayName)}
                        className="px-3 py-1.5 rounded-lg bg-tg-destructive/10 text-tg-destructive text-xs font-medium active:bg-tg-destructive/20"
                        type="button"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )
        ))
        .exhaustive()}
    </div>
  )
})

function SettingsRow({
  icon: Icon,
  label,
  value,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 active:bg-tg-secondary/50 transition-colors"
      type="button"
    >
      <Icon className="w-5 h-5 text-tg-accent" />
      <span className="flex-1 text-left text-[15px] text-tg-text">{label}</span>
      <span className="text-[13px] text-tg-hint capitalize">{value}</span>
      <ChevronRight className="w-4 h-4 text-tg-hint" />
    </button>
  )
}

function SettingsToggleRow({
  icon: Icon,
  label,
  checked,
  onChange,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className="w-full flex items-center gap-3 px-4 py-3 active:bg-tg-secondary/50 transition-colors"
      type="button"
    >
      <Icon className="w-5 h-5 text-tg-accent" />
      <span className="flex-1 text-left text-[15px] text-tg-text">{label}</span>
      <div
        className={`w-11 h-[26px] rounded-full relative transition-colors ${checked ? "bg-tg-accent" : "bg-tg-secondary"}`}
      >
        <div
          className={`absolute top-[3px] w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[22px]" : "translate-x-[3px]"}`}
        />
      </div>
    </button>
  )
}
